<script setup lang="ts">
// Identity pill (spec 2026-09-29, option A): replaces UserSwitcher.vue.
// Grey "Me ▾" pill for a confirmed superuser with no view-as target; turns
// amber with the target's label + a separate ✕ ("Back to me") once a
// target is set. A 3px amber stripe spans the top of the page as a
// constant reminder while viewing as someone else.
//
// Bug fix 1 (escape route must never vanish): the amber pill/✕/stripe
// render whenever useViewAs().target is set, NOT gated on isSuperUser —
// so a stale/failed auth read (network blip, cold API) can never make the
// "Back to me" control disappear while request() keeps sending the
// X-SideWinder-View-As header. Only the grey "Me ▾" pill (the normal,
// no-target state) requires a CONFIRMED superuser.
//
// `reload` is a prop (defaulting to window.location.reload) purely so
// tests can inject a spy instead of navigating a real jsdom location —
// same pattern UserSwitcher.vue used.
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { api, type SwitchableUser } from '../api/client'
import { useAuth } from '../composables/useAuth'
import { useViewAs } from '../composables/useViewAs'

const props = withDefaults(defineProps<{ reload?: () => void }>(), {
  reload: () => window.location.reload(),
})

const { isSuperUser, user } = useAuth()
const { target, set, clear } = useViewAs()
const users = ref<SwitchableUser[]>([])
const menuOpen = ref(false)

// The signed-in user must never see themselves in their own menu —
// GetAllActiveAsync (server) includes every active user, superuser
// included. Filtered client-side against /me's own userId, same pattern
// UserSwitcher.vue used.
const switchableUsers = computed(() => users.value.filter(u => u.userId !== user.value?.userId))

// The caller's own linked users, tagged "read-only" in the menu. #615
// added `isMine` to SwitchableUser (true when the row's guardian is the
// calling superuser) — a linked row that belongs to a DIFFERENT
// superuser's guardianship must fall into "other switchable users"
// (tagged by role) below, not here.
const linkedUsers = computed(() => switchableUsers.value.filter(u => u.isLinked && u.isMine))
const otherUsers = computed(() => switchableUsers.value.filter(u => !(u.isLinked && u.isMine)))

const avatarInitial = computed(() => {
  const label = target.value ? target.value.label : 'Me'
  return label.charAt(0).toUpperCase()
})

async function loadUsers() {
  try {
    users.value = await api.adminSwitchableUsers()
  } catch {
    users.value = []
  }
}

function maybeLoadUsers() {
  // Load whenever we're confirmed superuser, OR a target is already set
  // (the viewing state can render before /me resolves — e.g. a chart page
  // reload while viewing, or the network-failure case bug fix 1 covers —
  // and the menu must still be able to switch straight to another user).
  if (isSuperUser.value || target.value !== null) loadUsers()
}

onMounted(maybeLoadUsers)
// isSuperUser resolves asynchronously (after useAuth().init() answers
// /api/auth/me) — pick it up the moment it flips true. A getter (not the
// ref itself) so this stays a valid watch source even against test mocks
// that hand back a plain boolean.
watch(() => isSuperUser.value, (v) => { if (v) loadUsers() })

function toggleMenu() {
  menuOpen.value = !menuOpen.value
}

function selectUser(u: SwitchableUser) {
  menuOpen.value = false
  set({ userId: u.userId, label: u.label })
  props.reload()
}

function selectMe() {
  menuOpen.value = false
  if (target.value) {
    clear()
    props.reload()
  }
}

function backToMe() {
  clear()
  props.reload()
}

function onDocumentClick(ev: MouseEvent) {
  if (!menuOpen.value) return
  const el = ev.target as HTMLElement | null
  if (el && el.closest('.identity-pill-root')) return
  menuOpen.value = false
}

function onKeydown(ev: KeyboardEvent) {
  if (ev.key === 'Escape') menuOpen.value = false
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick)
  document.addEventListener('keydown', onKeydown)
})
onUnmounted(() => {
  document.removeEventListener('click', onDocumentClick)
  document.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <div v-if="isSuperUser || target !== null" class="identity-pill-root">
    <!-- Bug fix 1: this stripe (and the amber pill/✕ below) depend ONLY on
         `target`, never on isSuperUser — see the top-of-file note. -->
    <div
      v-if="target"
      class="viewing-as-stripe"
      data-testid="viewing-as-stripe"
      role="status"
      aria-label="Viewing as another user"
    />
    <!-- Round 2 layout fix: ▾ and ✕ are two segments of ONE pill (divided
         by a thin line), not a separate bordered ✕ box — the pill's visual
         height matches the other status-bar pills (~26px) on phone and
         desktop alike. Each segment's ≥44px touch target comes from a
         transparent ::before hit-area expansion (see .identity-pill-segment
         below), not from inflating the visible box. -->
    <div class="identity-pill-btn" :class="target ? 'pill-amber' : 'pill-grey'">
      <button
        type="button"
        class="identity-pill-segment identity-pill-toggle"
        data-testid="identity-pill"
        aria-haspopup="menu"
        :aria-expanded="menuOpen"
        @click.stop="toggleMenu"
      >
        <span class="identity-avatar">{{ avatarInitial }}</span>
        <span class="identity-label">{{ target ? target.label : 'Me' }}</span>
        <span class="identity-caret">&#9662;</span>
      </button>
      <template v-if="target">
        <span class="identity-pill-divider" aria-hidden="true"></span>
        <button
          type="button"
          class="identity-pill-segment identity-pill-close"
          data-testid="back-to-me-btn"
          aria-label="Back to me"
          @click.stop="backToMe"
        >&#10005;</button>
      </template>
    </div>

    <div v-if="menuOpen" class="identity-menu" data-testid="identity-menu" role="menu" @click.stop>
      <button
        type="button"
        class="identity-menu-item"
        data-testid="identity-menu-item-me"
        role="menuitem"
        @click.stop="selectMe"
      >
        <span>Me</span>
        <span v-if="!target" class="identity-menu-check">&#10003;</span>
      </button>
      <div class="identity-menu-divider" role="separator"></div>
      <button
        v-for="u in linkedUsers"
        :key="u.userId"
        type="button"
        class="identity-menu-item"
        role="menuitem"
        :data-testid="`identity-menu-item-${u.userId}`"
        @click.stop="selectUser(u)"
      >
        <span>{{ u.label }}</span>
        <span class="identity-menu-tag">read-only</span>
      </button>
      <button
        v-for="u in otherUsers"
        :key="u.userId"
        type="button"
        class="identity-menu-item"
        role="menuitem"
        :data-testid="`identity-menu-item-${u.userId}`"
        @click.stop="selectUser(u)"
      >
        <span>{{ u.label }}</span>
        <span class="identity-menu-tag">{{ u.role }}</span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.identity-pill-root {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.viewing-as-stripe {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: #d29922;
  z-index: 9999;
  pointer-events: none;
}

/* Round 2: the pill is ONE fixed-height chip (matches the other
   status-bar .sb-pill's visual size) containing one or two segments —
   the ▾ toggle, and (viewing state only) a divider + ✕ close segment.
   Height is a hard 26px on phone AND desktop; only the invisible pseudo-
   element hit areas below grow for touch, never this visible box. */
.identity-pill-btn {
  display: inline-flex;
  align-items: stretch;
  height: 26px;
  border-radius: 10px;
  font-size: 12px;
  font-family: 'JetBrains Mono', monospace;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
  box-sizing: border-box;
}
.pill-grey {
  background: #262626;
  border: 1px solid #3a3a3a;
  color: #ccc;
}
.pill-amber {
  background: #3a2f1b;
  border: 1px solid #d29922;
  color: #e3b341;
}

/* Each segment is its own button so its ::before hit-area expansion (see
   below) stays scoped to that segment — the toggle's expanded tap target
   must never swallow clicks meant for ✕, and vice versa. */
.identity-pill-segment {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 8px;
  background: transparent;
  border: none;
  color: inherit;
  font: inherit;
  cursor: pointer;
}
/* Transparent touch-target expansion: the visible segment stays ~26px
   tall (content-box ~24px), but this pseudo-element (part of the same
   clickable button box) extends the actual hit area well past ≥44px —
   top/bottom only (the `0` second value leaves left/right untouched) so
   neighbouring segments' tap areas never overlap. -13px each side gives
   ~24 + 26 = 50px total, comfortably clearing the 44px app convention
   with margin for sub-pixel rounding. */
.identity-pill-segment::before {
  content: '';
  position: absolute;
  inset: -13px 0;
}

.identity-avatar {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  flex-shrink: 0;
}
.identity-caret { font-size: 9px; }

/* Phone width (2026-11-04): a notch smaller so the status bar stays one row beside the phone icon. This block
   must come AFTER the base rules above: same specificity, so source order decides (#1201 placed the avatar rule
   before `.identity-avatar { display: inline-flex }` and the initial stayed visible — measured live). */
@media (max-width: 768px) {
  .identity-pill-btn { font-size: 11px; }
  .identity-pill-segment { padding: 0 6px; }
  /* The initial duplicates the label; dropping it on phones buys 22 px. */
  .identity-avatar { display: none; }
}

.identity-pill-divider {
  align-self: center;
  width: 1px;
  height: 14px;
  background: currentColor;
  opacity: 0.4;
}

.identity-pill-close { font-size: 11px; }
.identity-pill-segment:hover { background: rgba(255, 255, 255, 0.08); }
/* Keep the hover fill inside the pill's own rounded corners. */
.pill-grey .identity-pill-toggle { border-radius: 9px; }
.pill-amber .identity-pill-toggle { border-radius: 9px 0 0 9px; }
.pill-amber .identity-pill-close { border-radius: 0 9px 9px 0; }

.identity-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 180px;
  background: #1a1a1a;
  border: 1px solid #3a3a3a;
  border-radius: 6px;
  padding: 4px 0;
  z-index: 60;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}
.identity-menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 8px 12px;
  background: transparent;
  border: none;
  color: #ccc;
  text-align: left;
  font-size: 12px;
  font-family: 'JetBrains Mono', monospace;
  cursor: pointer;
}
.identity-menu-item:hover { background: #262626; }
.identity-menu-check { color: #4ade80; }
.identity-menu-tag { color: #888; font-size: 10px; }
.identity-menu-divider {
  height: 1px;
  background: #3a3a3a;
  margin: 4px 0;
}
</style>
